import { type ReactNode, useEffect, useState } from 'react';

import { AccessibilityWidget } from '@/components/AccessibilityWidget';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';

const KEY = 'hunk.age18';
const confirmed = () => {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * 18+ confirmation over the page (website and Mini App). Remembered in this browser.
 * The page renders underneath (hidden from people by the opaque overlay) so search engines, which render the page
 * but never click the button, still see the content - Google's advice for age interstitials.
 */
export function AgeGate({ children }: { children: ReactNode }) {
  const [ok, setOk] = useState(confirmed);
  // pinned: while the gate is up the page behind can't scroll (no peeking, no pull-to-refresh bounce)
  useEffect(() => {
    if (ok) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [ok]);
  // one tree for both states, so confirming does not remount the app
  const page = (
    <div className="contents" aria-hidden={ok ? undefined : true}>
      {children}
    </div>
  );
  if (ok) return <>{page}</>;

  const enter = () => {
    try {
      localStorage.setItem(KEY, '1');
    } catch {
      // private mode - ask again next time
    }
    setOk(true);
  };

  return (
    <>
      {page}
      {/* fixed full-screen overlay with no close button / backdrop click / Escape - the only ways out are the two buttons */}
      <main
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden overscroll-none pt-[var(--tg-top)] pb-[var(--tg-bottom)] bg-[radial-gradient(ellipse_at_top,#4a0a0d_0%,#000_60%)] p-4"
      >
        <div className="max-h-full w-full max-w-md overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-neutral-950 p-6 text-center shadow-2xl">
          <div className="flex justify-center">
            <Logo size="lg" className="mx-auto block" />
          </div>
          <div className="border-primary text-primary mx-auto mt-6 flex size-20 items-center justify-center rounded-full border-4 text-3xl font-extrabold">18+</div>
          <h1 className="mt-5 text-xl font-bold">האתר מיועד למבוגרים בלבד</h1>
          <p className="text-muted-foreground mt-3 text-sm leading-relaxed">החנות מוכרת מוצרים למבוגרים ומיועדת לבני 18 ומעלה בלבד. בכניסה לאתר את/ה מאשר/ת שגילך 18 ומעלה.</p>
          <div className="mt-6 flex flex-col gap-2">
            <Button size="lg" onClick={enter} className="rounded-full text-base font-bold">
              אני מעל גיל 18 - כניסה
            </Button>
            <Button size="lg" variant="secondary" className="rounded-full" asChild>
              <a href="https://www.google.com">יציאה</a>
            </Button>
          </div>
        </div>
        {/* the header (with the accessibility button) is under this overlay - so it gets its own */}
        <AccessibilityWidget floating />
      </main>
    </>
  );
}
